import { createElement } from 'react';
import { RouteObject, redirect } from 'react-router-dom';

// Components
import Layout from 'components/layout/Layout';
import Error404 from 'components/navigation/Error404';

// Helpers
import { IPage } from 'features/page/pageAPI';
import { SLUGS } from 'helpers/consts';
import { RouteConfig, componentMap, formComponentMap } from '../routes';

/**
 * Gets all restricted route slugs from the route configuration
 * Used to validate if the current route is restricted without hardcoding slugs
 */
export function getRestrictedRouteSlugs(): string[] {
  // Import dynamically to avoid circular dependencies
  const { staticRoutes } = require('../routes');
  return staticRoutes
    .filter((config: RouteConfig) => config.restricted)
    .map((config: RouteConfig) => {
      // Extract slug from path (e.g., '/profile' -> 'profile')
      const pathParts = config.path.split('/').filter(Boolean);
      return pathParts[0] || null;
    })
    .filter((slug: string | null): slug is string => slug !== null);
}

/**
 * Gets all restricted route slugs from the effective route configuration.
 * Database pages can override static routes, so use the merged route list.
 */
export function getRestrictedRouteSlugsForPages(pages: IPage[]): string[] {
  const { generateRoutesWithPages } = require('../routes');
  return generateRoutesWithPages(pages)
    .filter((config: RouteConfig) => config.restricted)
    .map((config: RouteConfig) => {
      const pathParts = config.path.split('/').filter(Boolean);
      return pathParts[0] || null;
    })
    .filter((slug: string | null): slug is string => slug !== null);
}

/**
 * Creates layout props based on the route configuration
 */
function getLayoutProps(config: RouteConfig) {
  const props: Record<string, boolean> = {};
  // Convert layout value to a boolean prop
  if (config.layout) {
    props[config.layout] = true;
  }
  // Handle restricted
  if (config.restricted) {
    props.restricted = true;
  }
  return props;
}

/**
 * Creates the appropriate loader function for a route
 */
function createLoader(config: RouteConfig) {
  // Root redirect loader
  // if (config.path === '/' && config.loader) {
  //   return async () => {
  //     const isLoggedIn = getUserIdFromToken();
  //     return redirect(`/${isLoggedIn ? SLUGS.MYPROJECTS : SLUGS.WELCOME}`);
  //   };
  // }
  // Redirect loader
  if (config.redirect) {
    return async () => {
      return redirect(config.redirect!);
    };
  }
  // Default loader for routes that need params
  if (config.params) {
    return ({ params }: { params: any }) => params;
  }
  return undefined;
}

/**
 * Creates a route element with the appropriate layout and component
 */
function createRouteElement(config: RouteConfig) {
  const Component = componentMap[config.component as keyof typeof componentMap];
  if (!Component) {
    console.warn(`Component not found for path: ${config.component}`);
    return createElement(Layout, { white: true }, createElement(Error404));
  }
  const layoutProps = getLayoutProps(config);
  const componentProps = createComponentProps(config);
  const componentElement = createElement(Component as any, componentProps);
  return createElement(Layout, layoutProps, componentElement);
}

/**
 * Creates props for the component based on the route configuration
 */
function createComponentProps(config: RouteConfig) {
  const props: any = {};
  // Always pass slug and className if present
  if (config.slug) {
    props.slug = SLUGS[config.slug as keyof typeof SLUGS] || config.slug;
  }
  if (config.className) {
    props.className = config.className;
  }
  // ModalPage specific props
  if (config.component === 'ModalPage') {
    if (config.formComponent) {
      props.formComponent = formComponentMap[config.formComponent];
    }
    if (config.formComponentProps) {
      props.formComponentProps = config.formComponentProps;
    }
    if (config.backdropSlug) {
      props.backdropSlug = config.backdropSlug;
    }
    if (config.navigateOnClose) {
      props.navigateOnClose = config.navigateOnClose;
    }
  }
  // Merge in generic componentProps if present (do not overwrite slug/className)
  if (config.componentProps) {
    Object.entries(config.componentProps).forEach(([key, value]) => {
      if (!(key in props)) {
        props[key] = value;
      }
    });
  }
  return props;
}

/**
 * Builds React Router routes from the configuration
 */
export function buildRoutes(configs: RouteConfig[]): RouteObject[] {
  return configs.map((config) => {
    const fullPath = config.path + (config.params || '');
    const route: RouteObject = {
      path: fullPath,
      element: createRouteElement(config),
    };
    const loader = createLoader(config);
    if (loader) {
      route.loader = loader;
    }
    return route;
  });
}
